// © Microsoft Corporation. All rights reserved.

import {
    AppError,
    SocialExperiencePublisherFollowUnfollowError,
    SocialExperiencePublisherIsFollowedFetchError,
    SocialExperienceReducerIncorrectArgumentError,
    logger
} from "@msnews/diagnostics";
import { BridgeEventNames, dispatchBridgeEvent } from "./SocialData.bridge";
import { Content, ItemTypes, SocialService, SocialServiceMapper, TypingUpdates, TypingUser, User, getParameter } from "@msnews/social-data-service";
import { GradientOverlayData, PublisherSubscribeFollowButtonData, SocialDataState } from "./SocialData.state";
import { PublisherActions, PublisherServiceClient } from "@msnews/publishers-service-client";
import { SignalRMessage, WebSockets } from "./utils/webSockets";

import { DataConnector } from "@msnews/experiences-redux";
import { SocialDataActions } from "./SocialData.actions";
import { appErrorLogger } from "./utils/AppErrorLogger";
import { commentExists } from "./utils/parseState";
import { enums } from "./utils/enums";
import { isComponentDisabled } from "./utils/themeHelpers";
import { selectContentId } from "./utils/contentIdKey";

declare global {
    interface Window {
        signalR: any;
        AzureStorage: any;
        ReactNativeWebView: any;
    }
}

let isFetchUserCommunitySettingsCalled = false;

/**
 * The connector for the SocialData Experience.
 */
export class SocialDataConnector extends DataConnector<any, SocialDataState, any> {
    private publisherServiceClient?: PublisherServiceClient;

    public getSocialBarConnectorFunctionsWithStateBindings() {
        const state = this.getCurrentState() as SocialDataState;
        return {
            checkAndLogReducerError: this.checkAndLogReducerError.bind(this),
            styleConnector: {
                updateResponsiveClass: this.updateResponsiveClass.bind(this)
            },
            socialBarConnector: {
                onSocialBarComponentConnected: this.onSocialBarComponentConnected.bind(this, state.me, state.userCommunitySettings),
                sendContentReaction: this.sendContentReaction.bind(this),
                isUserCommunitySettingsFetched: this.isUserCommunitySettingsFetched.bind(this, state.userCommunitySettings)
            },
            popupConnector: {
                openPopup: this.openPopup.bind(this, state.theme),
                closePopup: this.closePopup.bind(this, state.popup)
            }
        };
    }

    public getSocialCommentConnectorFunctionsWithStateBindings() {
        const state = this.getCurrentState() as SocialDataState;
        return {
            checkAndLogReducerError: this.checkAndLogReducerError.bind(this),
            styleConnector: {
                updateResponsiveClass: this.updateResponsiveClass.bind(this)
            },
            commentConnector: {
                onCommentComponentConnect: this.onCommentComponentConnect.bind(this, state.me, state.userCommunitySettings),
                submitComment: this.submitComment.bind(this, state.me, state.theme),
                deleteComment: this.deleteComment.bind(this, state.me, state.theme),
                appealComment: this.appealComment.bind(this),
                sendCommentReaction: this.sendCommentReaction.bind(this),
                showMoreComments: this.showMoreComments.bind(this),
                showMoreReplies: this.showMoreReplies.bind(this),
                loadMoreComments: this.loadMoreComments.bind(this),
                loadPreviousComments: this.loadPreviousComments.bind(this),
                onSortTypeChanged: this.onSortTypeChanged.bind(this),
                updateTypers: this.updateTypers.bind(this),
                broadcastAsTyping: this.broadcastAsTyping.bind(this, state.me),
                listenForTypers: this.listenForTypers.bind(this),
                isUserCommunitySettingsFetched: this.isUserCommunitySettingsFetched.bind(this, state.userCommunitySettings)
            },
            usersConnector: {
                fetchUser: this.fetchUser.bind(this, state.users),
                fetchIsUserFollowed: this.fetchIsUserFollowed.bind(this, state.users, state.me),
                followUser: this.followUser.bind(this, state.users, state.me, state.theme),
                createUser: this.createUser.bind(this, state.me),
                fetchMe: this.fetchMe.bind(this),
                appealMe: this.appealMe.bind(this),
                appealMyPicture: this.appealMyPicture.bind(this),
                fetchActivities: this.fetchActivities.bind(this, state.activities),
                fetchNotifications: this.fetchNotifications.bind(this, state.me),
                markActivityAsRead: this.markActivityAsRead.bind(this)
            },
            popupConnector: {
                openPopup: this.openPopup.bind(this, state.theme),
                closePopup: this.closePopup.bind(this, state.popup)
            }
        };
    }

    public getSocialProfileConnectorFunctionsWithStateBindings() {
        const state = this.getCurrentState() as SocialDataState;
        return {
            checkAndLogReducerError: this.checkAndLogReducerError.bind(this),
            styleConnector: {
                updateResponsiveClass: this.updateResponsiveClass.bind(this)
            },
            profileConnector: {
                onProfilePageComponentConnected: this.onProfilePageComponentConnected.bind(this, state.users, state.me),
                fetchUserFollowers: this.fetchUserFollowers.bind(this, state.users),
                fetchUserFollowings: this.fetchUserFollowings.bind(this, state.users),
                fetchUserComments: this.fetchUserComments.bind(this, state.users),
                setProfilePrivate: this.setProfilePrivate.bind(this),
                deleteMyData: this.deleteMyData.bind(this, state.me, state.users),
                initializePublisherServiceClient: this.initializePublisherServiceClient.bind(this),
                followPublisher: this.followPublisher.bind(this),
                getPublisherIsFollowing: this.getPublisherIsFollowing.bind(this)
            },
            commentConnector: {
                appealComment: this.appealComment.bind(this),
                deleteComment: this.deleteComment.bind(this, state.me, state.theme)
            },
            usersConnector: {
                fetchUser: this.fetchUser.bind(this, state.users),
                fetchIsUserFollowed: this.fetchIsUserFollowed.bind(this, state.users, state.me),
                followUser: this.followUser.bind(this, state.users, state.me, state.theme),
                createUser: this.createUser.bind(this, state.me),
                fetchMe: this.fetchMe.bind(this),
                appealMe: this.appealMe.bind(this),
                appealMyPicture: this.appealMyPicture.bind(this),
                fetchActivities: this.fetchActivities.bind(this, state.activities),
                fetchNotifications: this.fetchNotifications.bind(this, state.me),
                markActivityAsRead: this.markActivityAsRead.bind(this)
            },
            popupConnector: {
                openPopup: this.openPopup.bind(this, state.theme),
                closePopup: this.closePopup.bind(this, state.popup)
            }
        };
    }

    public getSocialConnectorFunctionsWithStateBindings() {
        const state = this.getCurrentState() as SocialDataState;
        return {
            checkAndLogReducerError: this.checkAndLogReducerError.bind(this),
            styleConnector: {
                updateResponsiveClass: this.updateResponsiveClass.bind(this)
            },
            profileConnector: {
                deleteMyData: this.deleteMyData.bind(this, state.me, state.users)
            },
            usersConnector: {
                fetchUser: this.fetchUser.bind(this, state.users),
                fetchIsUserFollowed: this.fetchIsUserFollowed.bind(this, state.users, state.me),
                followUser: this.followUser.bind(this, state.users, state.me, state.theme),
                createUser: this.createUser.bind(this, state.me),
                fetchMe: this.fetchMe.bind(this),
                appealMe: this.appealMe.bind(this),
                appealMyPicture: this.appealMyPicture.bind(this),
                fetchActivities: this.fetchActivities.bind(this, state.activities),
                fetchNotifications: this.fetchNotifications.bind(this, state.me),
                markActivityAsRead: this.markActivityAsRead.bind(this)
            },
            reactionBreakdownConnector: {
                onReactionBreakdownComponentConnected: this.onReactionBreakdownComponentConnected.bind(this, state.me, state.contents),
                onReactionBreakdownComponentDisconnected: this.onReactionBreakdownComponentDisconnected.bind(this)
            },
            popupConnector: {
                openPopup: this.openPopup.bind(this, state.theme),
                closePopup: this.closePopup.bind(this, state.popup)
            },
            publisherSubscribeFollowButtonConnector: {
                initializePublisherServiceClient: this.initializePublisherServiceClient.bind(this),
                followPublisher: this.followPublisher.bind(this),
                getPublisherIsFollowing: this.getPublisherIsFollowing.bind(this)
            }
        };
    }

    public getBeaconConnectorFunctionsWithStateBindings() {
        const state = this.getCurrentState() as SocialDataState;
        return {
            popupConnector: {
                openPopup: this.openPopup.bind(this, state.theme),
                closePopup: this.closePopup.bind(this, state.popup)
            }
        };
    }

    public async setTheme(theme?: string) {
        SocialDataActions.setTheme.getActionSender(this).send(theme);
    }

    public async setGradientOverlayData(data: GradientOverlayData) {
        SocialDataActions.setGradientOverlayData.getActionSender(this).send(data);
    }

    public async setPublisherSubscribeFollowButtonData(data: PublisherSubscribeFollowButtonData) {
        SocialDataActions.setPublisherSubscribeFollowButtonData.getActionSender(this).send(data);
    }

    public async onCommentComponentConnect(me, userCommunitySettings, contentInfo, specificCommentId?) {
        if (!me) {
            this.fetchMe();
        }
        if (!isFetchUserCommunitySettingsCalled) {
            this.fetchUserCommunitySettings();
        }

        const content = await this.getAndUpdateContent(contentInfo);
        if (content.notCreated) {
            return;
        }

        if (specificCommentId) {
            this.loadSpecificComment(content.id, specificCommentId).catch(
                () => SocialDataActions.resetSpecificCommentId.getActionSender(this).send()
            );
        } else {
            this.loadAllComments(content.id, contentInfo);
        }

        return content;
    }

    public async loadSpecificComment(contentId, commentId) {
        const comment = await SocialService.getCommentById(commentId);
        if (!comment || comment.contentId !== contentId) {
            throw new Error("The specific comment doesn't belong to this content");
        }
        const isReply = comment.parentId;

        if (isReply) {
            const commentsBefore = await SocialService.getCommentsByAnchorCommentId(comment.id, {top: 2, orderBy: "time desc"});
            const commentsAfter = await SocialService.getCommentsByAnchorCommentId(comment.id, {top: 2, orderBy: "time asc"});
            const replies = {
                ...{...commentsBefore, hasPrevious: commentsBefore.hasMore},  // since it is reversed
                list: commentsBefore.list.reverse().concat(commentsAfter.list.slice(1)), // get rid of one of the anchor comments
                hasMore: commentsAfter.hasMore
            };

            const parentAndItsAround = await SocialService.getCommentsByAnchorCommentId(comment.parentId, {top: 1, orderBy: "rating"});
            SocialDataActions.insertComments.getActionSender(this).send(contentId, parentAndItsAround, false);
            SocialDataActions.insertComments.getActionSender(this).send(contentId, replies, false, comment.parentId);

            this.updateCommentRatings(contentId, parentAndItsAround.list.concat(replies.list));
        } else {
            const commentsBefore = await SocialService.getCommentsByAnchorCommentId(comment.id, {top: 2, orderBy: "rating asc"});
            const commentsAfter = await SocialService.getCommentsByAnchorCommentId(comment.id, {top: 2, orderBy: "rating desc"});
            const commentAndItsAround = {
                ...{...commentsBefore, hasPrevious: commentsBefore.hasMore},  // since it is reversed
                list: commentsBefore.list.reverse().concat(commentsAfter.list.slice(1)),  // get rid of one of the anchor comments
                hasMore: commentsAfter.hasMore
            };
            SocialDataActions.insertComments.getActionSender(this).send(contentId, commentAndItsAround, false);

            this.updateCommentRatings(contentId, commentAndItsAround.list);
        }

    }

    public async loadAllComments(contentId, contentInfo) {
        const topCommentsToFetchCount = contentInfo.top || 2;
        const skippedCommentsCount = contentInfo.skip || 0;

        SocialService.getCommentsByContentId(contentId, {top: topCommentsToFetchCount, skip: skippedCommentsCount}).then(comments => {
            SocialDataActions.insertComments.getActionSender(this).send(contentId, comments, false);
            this.updateCommentRatings(contentId, comments.list);
        });
    }

    public showMoreComments(contentId, top, skip, sortType) {
        SocialService.getCommentsByContentId(contentId, {top, skip, sortType})
            .then(comments => {
                SocialDataActions.insertComments.getActionSender(this).send(contentId, comments, true);
                this.updateCommentRatings(contentId, comments.list);
            });
    }

    public onSortTypeChanged(contentId, top, sortType) {
        const params = {top, skip: 0, sortType};
        SocialService.getCommentsByContentId(contentId, params)
            .then(comments => {
                SocialDataActions.insertComments.getActionSender(this).send(contentId, comments, false);
                this.updateCommentRatings(contentId, comments.list);
            });
        SocialDataActions.resetSpecificCommentId.getActionSender(this).send();
    }

    public loadCommentsUsingAnchorComment(contentId, anchorCommentId, loadPrevious: boolean, parentCommentId?) {
        const isReply = parentCommentId;
        const orderBy = isReply ? "time" : "rating";
        // 'previous' definition of time and rating ordering is different since one of them is ascending and the other is descending
        const asc = orderBy === "time" ? !loadPrevious : loadPrevious;
        const prepending = (orderBy === "time" && !asc) || (orderBy === "rating" && asc);
        SocialService.getCommentsByAnchorCommentId(anchorCommentId, {top: 5, orderBy: `${orderBy} ${asc ? "asc" : "desc"}`})
            .then(comments => {
                let commentList = comments.list.slice(1); // get rid of anchor comment, since it is already loaded
                // `hasMore` and `hasPrevious` is meaningful only in the direction we are looking for
                // because starting point is the anchor comment
                if (prepending) {
                    commentList = commentList.reverse();
                    comments.hasPrevious = comments.hasMore;
                    comments.hasMore = undefined;
                } else {
                    comments.hasPrevious = undefined;
                }
                comments.list = commentList;
                SocialDataActions.insertComments.getActionSender(this).send(contentId, comments, true, parentCommentId, prepending);
                this.updateCommentRatings(contentId, comments.list);
            });
    }

    public loadMoreComments(contentId, lastCommentId, parentCommentId?) {
        this.loadCommentsUsingAnchorComment(contentId, lastCommentId, false, parentCommentId);
    }

    public loadPreviousComments(contentId, firstCommentId, parentCommentId?) {
        this.loadCommentsUsingAnchorComment(contentId, firstCommentId, true, parentCommentId);
    }

    public showMoreReplies(contentId, commentId, top, skip, orderBy) {
        const params = {top, skip, orderBy};

        SocialService.getCommentsByParentId(commentId, params)
            .then(comments => {
                SocialDataActions.insertComments.getActionSender(this).send(contentId, comments, true, commentId);
                this.updateCommentRatings(contentId, comments.list);
            });
    }

    public updateCommentRatings(contentId, commentList) {
        commentList.forEach(comment => {
            SocialService.getRating(ItemTypes.comments, comment.id)
                .then((rating) => {
                    SocialDataActions.updateCommentRating.getActionSender(this).send(contentId, comment.id, rating, false);
                });
        });
    }

    public handleUserLoginFromState(me, theme): boolean {
        if (!me) {
            return false;
        }
        if (me.state == enums.userStateType.anonymous) {
            this.openPopup(theme, "multiLoginPage");
            return false;
        } else if (me.state == enums.userStateType.newUser) {
            this.openPopup(theme, "onboardingPage");
            return false;
        }
        return true;
    }

    async submitComment(me, theme, content, body, commentParentId, commentMentionReplyId) {
        // If comment value is empty string then terminate
        if (body.trim().length < 1) {
            return;
        }

        if (!this.handleUserLoginFromState(me, theme)) {
            return;
        }

        try {
            let responseComment;
            await SocialService
                .postComment(content.id, me.id, body, commentParentId, commentMentionReplyId)
                .then(response => Object.assign(response, { user: me }))
                .then(comment => {
                    responseComment = comment;
                    SocialDataActions.insertComment.getActionSender(this).send(content.id, comment, commentParentId, true);
                    const hubName = (comment as any).contentId;
                    SocialService.broadcastCommentNotification(hubName, (comment as any).id, commentParentId);
                })
                .catch(response => {
                    try {
                        if (response) {
                            responseComment = { errorCode: response.code, errorSubCodes: response.subCodes };
                        }
                    } catch (e) {
                        responseComment = { errorCode: 0 };
                    }
                });
            return responseComment;
        } catch (error) {
            logger.logError(`Error: comment post failed => ${JSON.stringify(error)}`);
            return false;
        }
    }

    public deleteComment(me, theme, commentId, parentId, userId) {
        if (!this.handleUserLoginFromState(me, theme)) {
            return;
        }

        SocialService.deleteComment(commentId)
            .then(_ => {
                SocialDataActions.deleteUserComment.getActionSender(this).send(userId, commentId, parentId);
            });
    }

    public sendCommentReaction(comment, rateValue, reset) {
        if (reset) {
            SocialService.resetRating(ItemTypes.comments, comment.id)
                .then(_ => {
                    SocialDataActions.updateCommentRating.getActionSender(this).send(comment.contentId, comment.id, undefined, true);
                });
        } else {
            SocialService.rate(ItemTypes.comments, comment.id, rateValue, false)
                .then(rating => {
                    SocialDataActions.updateCommentRating.getActionSender(this).send(comment.contentId, comment.id, rating, true);
                });
        }
    }

    public async sendContentReaction(content, rateValue, reset) {
        if (reset) {
            SocialService.resetRating(ItemTypes.contents, content.id, window.ReactNativeWebView && this.saveReactionInSuperAppLocalStorage)
                .then(_ => {
                    SocialDataActions.updateContentRating.getActionSender(this).send(content.id, undefined);
                });
        } else {
            SocialService.rate(ItemTypes.contents, content.id, rateValue, false, window.ReactNativeWebView && this.saveReactionInSuperAppLocalStorage)
                .then(rating => {
                    SocialDataActions.updateContentRating.getActionSender(this).send(content.id, rating);
                });
        }
    }

    public async getContent(contentInfo) {
        const { id: cmsId, market } = contentInfo;
        let localContent = this.getLocalContent(cmsId, market);
        if (localContent) {
            return localContent;
        }

        const content = await SocialService.getContentById(cmsId, market);

        // Concurrency control
        localContent = this.getLocalContent(cmsId, market);
        if (localContent) {
            return localContent;
        }
        SocialDataActions.setContent.getActionSender(this).send(content, content.id || selectContentId({}, cmsId, market));
        SocialDataActions.mapCmsIdToContentId.getActionSender(this).send(cmsId, market, content.id || selectContentId({}, cmsId, market));
        return content;
    }

    private getLocalContent(cmsId: string, market: string) {
        const state: SocialDataState = this.getCurrentState();
        return state && state.contents && state.contents[selectContentId({}, cmsId, market)];
    }

    public async getAndUpdateContent(contentInfo) {
        const content = await this.getContent(contentInfo);
        if (!content.notCreated) {
            SocialService.getRating(ItemTypes.contents, content.id).then(rating => {
                SocialDataActions.updateContent.getActionSender(this).send(content.id, {rating});
            });
        }
        return content;
    }

    public fetchUser(users, userId, force=false) {
        const user = users && users[userId];
        if (!force && user && (user.isFetched || user.isFetching)) { return Promise.resolve(); }

        SocialDataActions.updateUser.getActionSender(this).send(userId, { isFetching: true });
        return SocialService.getUserById(userId)
            .then(user => {
                SocialDataActions.updateUser.getActionSender(this).send(userId, { data: user, isFetched: true, isFetching: false });
            })
            .catch(e => SocialDataActions.updateUser.getActionSender(this).send(userId, { isFetching: false }));
    }

    public fetchIsUserFollowed(users, me, userId, force=false) {
        const user = users && users[userId];
        if (!force && user && (user.isFollowingFetched || user.isFollowingFetching)) { return Promise.resolve(); }

        SocialDataActions.updateUser.getActionSender(this).send(userId, { isFollowingFetching: true });
        return SocialService.getUserIsFollowed(me.id, userId)
            .then(result => {
                const newUser = { data: { isFollowing: result }, isFollowingFetched: true, isFollowingFetching: false };
                SocialDataActions.updateUser.getActionSender(this).send(userId, newUser);
            })
            .catch(e => SocialDataActions.updateUser.getActionSender(this).send(userId, { isFollowingFetching: false }));
    }

    public async followUser(users, me, theme, userId, follow) {
        if (!this.handleUserLoginFromState(me, theme)) {
            return;
        }

        if (follow) {
            SocialService.followUser(me.id, userId)
                .then(_ => {
                    const newUser = { data: { isFollowing: true,
                        followers: users[userId].data.followers && {...users[userId].data.followers, list: users[userId].data.followers.list.concat(users[me.id].data)},
                        followersCount: users[userId].data.followersCount + 1 }, isFollowingFetched: true, isFollowingFetching: false
                    };
                    const newMe = { data: {
                        following: users[me.id].data.following && {...users[me.id].data.following, list: users[me.id].data.following.list.concat(users[userId].data)},
                        followingCount: users[me.id].data.followingCount + 1 }, isFollowingFetched: true, isFollowingFetching: false
                    };
                    SocialDataActions.updateUser.getActionSender(this).send(userId, newUser);
                    SocialDataActions.updateUser.getActionSender(this).send(me.id, newMe);
                });
        } else {
            SocialService.unfollowUser(me.id, userId)
                .then(_ => {
                    const newUser = { data: { isFollowing: false,
                        followers: users[userId].data.followers && {...users[userId].data.followers, list: users[userId].data.followers.list.filter(x => x.id != users[me.id].data.id)},
                        followersCount: users[userId].data.followersCount - 1 }, isFollowingFetched: true, isFollowingFetching: false };
                    const newMe = { data: {
                        following: users[me.id].data.following && {...users[me.id].data.following, list: users[me.id].data.following.list.filter(x => x.id != users[userId].data.id)},
                        followingCount: users[me.id].data.followingCount - 1 }, isFollowingFetched: true, isFollowingFetching: false
                    };
                    SocialDataActions.updateUser.getActionSender(this).send(userId, newUser);
                    SocialDataActions.updateUser.getActionSender(this).send(me.id, newMe);
                });
        }
    }

    public async createUser(me) {
        let newUser = await SocialService.createUser();
        newUser = {...me, ...newUser};
        SocialDataActions.updateMe.getActionSender(this).send(newUser);
    }

    public async onSocialBarComponentConnected(me, userCommunitySettings, contentInfo) {
        if (!me) {
            me = await this.fetchMe();
        }
        if (!isFetchUserCommunitySettingsCalled) {
            this.fetchUserCommunitySettings();
        }
        const content = await this.getContent(contentInfo);
        if (!content.notCreated) {
            const cachedMyReaction = getParameter("cachedMyReaction");
            if (cachedMyReaction) {
                content.rating = cachedMyReaction;
                SocialDataActions.updateContent.getActionSender(this).send(content.id, {rating: cachedMyReaction});
            }
            SocialService.getFriendRatings(ItemTypes.contents, content.id).then(ratings => {
                this.processRatings(content.id, me, ratings, cachedMyReaction);
            });
        }
        return content;
    }

    public processRatings(contentId, me, ratings, cachedMyReaction) {
        if (!ratings || !ratings.items) {
            return;
        }
        const friendRatings = {};
        ratings.items.map(rating => {
            const userRecord = rating.links.find(x => x.type == "User");
            if (userRecord && userRecord.item) {
                SocialDataActions.updateUser.getActionSender(this).send(userRecord.item.id, { data: SocialServiceMapper.convertUser(userRecord.item), isFetched: true, isFetching: false });
                friendRatings[userRecord.item.id] = rating.subType.toLowerCase();
            }
        });
        if (!cachedMyReaction && me && me.id in friendRatings) {
            SocialDataActions.updateContent.getActionSender(this).send(contentId, {rating: friendRatings[me.id]});
            delete friendRatings[me.id];
        }
        if (!cachedMyReaction && me && me.id) {
            const myAnonReaction = ratings.items.find(x => x.sourceId == me.id);
            if (myAnonReaction && myAnonReaction.subType) {
                SocialDataActions.updateContent.getActionSender(this).send(contentId, {rating: myAnonReaction.subType.toLowerCase()});
            }
        }

        SocialDataActions.updateContent.getActionSender(this).send(contentId, {friendRatings});
    }

    public async fetchUserFollowers(users, userId) {
        const followers = users && users[userId] && users[userId].data && users[userId].data.followers;
        if (followers && followers.hasMore === false) { return; }
        return SocialService.getFollowersByUserId(userId, 10, (followers && followers.list) ? followers.list.length : 0)
            .then(followers => {
                return SocialDataActions.updateUserFollowers.getActionSender(this).send(userId, followers);
            });
    }

    public async fetchUserFollowings(users, userId) {
        const following = users && users[userId] && users[userId].data && users[userId].data.following;
        if (following && following.hasMore === false) { return; }
        return SocialService.getFollowingsByUserId(userId, 10, (following && following.list) ? following.list.length : 0)
            .then(following => {
                return SocialDataActions.updateUserFollowing.getActionSender(this).send(userId, following);
            });
    }

    public fetchUserComments(users, userId, reset, orderBy) {
        let skip = 0;
        if (!reset && users && users[userId] && users[userId].data && users[userId].data.comments && users[userId].data.comments.list) {
            skip = users[userId].data.comments.list.length;
        }
        return SocialService.getCommentsByUserId(userId, 10, skip, orderBy)
            .then(comments => {
                return SocialDataActions.updateUserComments.getActionSender(this).send(userId, comments, reset);
            });
    }

    public async fetchActivities(activities) {
        if (activities && activities.hasMore === false) { return; }
        return SocialService.getUserActivities(10, (activities && activities.list) ? activities.list.length : 0)
            .then(activities => {
                return SocialDataActions.updateActivities.getActionSender(this).send(activities);
            });
    }

    public async fetchNotifications(me) {
        return me && me.isSocialUser && SocialService.getUserNotifications()
            .then(notifications => {
                if (notifications && notifications.list && notifications.list.length > 0) {
                    return SocialDataActions.updateNotifications.getActionSender(this).send(notifications);
                }
            });
    }

    public async markActivityAsRead(id, updateReduxStore = false) {
        return SocialService.updateUserActivityStatus(id, true)
            .then(_ => {
                if (updateReduxStore) {
                    SocialDataActions.updateActivityStatus.getActionSender(this).send(id, true);
                }
            });
    }

    public async fetchMe(verify = false) {
        return new Promise((resolve, reject) => SocialService.fetchMe(verify).then((user) => {
            SocialDataActions.updateMe.getActionSender(this).send(user);
            resolve(user);
        }));
    }

    public async fetchUserCommunitySettings() {
        isFetchUserCommunitySettingsCalled = true;
        const communitySettings = await SocialService.fetchUserCommunitySettings();
        SocialDataActions.updateUserCommunitySettings.getActionSender(this).send(communitySettings);
    }

    public isUserCommunitySettingsFetched(userCommunitySettings): boolean {
        // When `userCommunitySettings` is fetched but user has no settings, `userCommunitySettings` is `null`
        // When it is not fetched yet, it is `undefined`
        return userCommunitySettings !== undefined;
    }

    public async onReactionBreakdownComponentConnected(me, contents, data) {
        if (!me) {
            this.fetchMe();
        }

        let content = data.contentId && contents && contents[data.contentId];
        if (data.contentId && !content) {
            const contentInfoArray = data.contentId.split("_");
            content = await this.getContent({id: contentInfoArray[0], market: contentInfoArray[1]});
        }

        let reactions;
        if (data.commentId || data.contentId) {
            reactions = await SocialService.getRatingBreakdown(data.commentId ? ItemTypes.comments : ItemTypes.contents, data.commentId || data.contentId, content);
        } else {
            console.warn("Could not get reaction breakdown info. Missing input parameter; please provide commentId, contentId, pageUrl or canonicalUrl");
        }

        const newItems = { };
        Object.keys(reactions.items).forEach(key => {
            newItems[key] = reactions.items[key].map(item => {
                const user = item.user;
                const userId = user.id;
                SocialDataActions.updateUser.getActionSender(this).send(userId, { data: SocialServiceMapper.convertUser(user), isFetched: true, isFetching: false });
                const newItem = { ...item, userId };
                delete newItem.user;
                return newItem;
            });
        });
        reactions.items = newItems;
        SocialDataActions.setReactionBreakdown.getActionSender(this).send({reactions});
    }

    public async onReactionBreakdownComponentDisconnected() {
        const emptyReactions = {reactions: {reactionCounts: {all: 0, like: 0, love: 0, angry: 0, surprised: 0, thinking: 0, sad: 0}, items: { all: [], like: [], love: [], angry: [], surprised: [], thinking: [], sad: []}}};
        SocialDataActions.setReactionBreakdown.getActionSender(this).send(emptyReactions);
    }

    public async onProfilePageComponentConnected(users, me, userId) {
        if (!me) {
            me = await this.fetchMe();
        }

        const id = userId == "me" ? me.id : userId;

        SocialService.getUserProfile(id)
            .then(data => {
                SocialDataActions.updateUser.getActionSender(this).send(id, { data: data, isFetched: true, isFetching: false });
            });

        this.fetchIsUserFollowed(users, me, id);
    }

    public setProfilePrivate(userId, isPrivate) {
        SocialService.updateUserSettings(userId, isPrivate)
            .then(_ => {
                const data = {isPrivateProfile: isPrivate};
                SocialDataActions.updateUser.getActionSender(this).send(userId, { data: data });
            });
    }

    public openPopup(theme, moduleName, props?) {
        if (isComponentDisabled(theme, moduleName.toLowerCase())) {
            if (moduleName.toLowerCase() === "multiloginpage") {
                dispatchBridgeEvent(BridgeEventNames.signInClicked);
            } else {
                dispatchBridgeEvent(BridgeEventNames.openPopup, {component: moduleName.toLowerCase(), id: props && props.id, fullScreen: props && props.fullScreen || false});
            }
        } else {
            SocialDataActions.setPopup.getActionSender(this).send(moduleName, props);
        }
    }

    public closePopup(popup, props?) {
        if (popup && popup.moduleName) {
            SocialDataActions.setPopup.getActionSender(this).send(undefined, undefined);
        } else {
            // Do not close the report menu modal in order to get the copyright form open message from web view
            if (!(props && props.disableBridge)) {
                dispatchBridgeEvent(BridgeEventNames.closePopup, {refreshPage: (props && props.refreshPage) || false});
            }
        }
    }

    public async listenForTypers(id: string, market: string, content: Content) {
        const contentId = (content && content.id) || (id && market && id + "_" + market);
        if (contentId && (!content || !content.typingUpdates || !content.typingUpdates.hubName || content.typingUpdates.hubName !== contentId)) {
            SocialDataActions.recordSignalRHub.getActionSender(this).send(contentId);
            WebSockets.open(contentId, this.handleSignalRMessage.bind(this, contentId, id, market));
        }
    }

    public async handleSignalRMessage(contentId: string, id: string, market: string, message: SignalRMessage) {
        if (message.type === "typing-notification") {
            const typer: TypingUser = message.data;
            typer.lastTypingTime = Date.now();
            SocialDataActions.addTyper.getActionSender(this).send(typer, contentId, id, market);
        } else if (message.type === "comment-notification") {
            const state: SocialDataState = this.getCurrentState();
            const content = state && state.contents && state.contents[contentId];
            const { commentId } = message.data;
            const commentList = content.comments && content.comments.list;
            if (commentExists(commentList, commentId)) {
                // we've already loaded this comment
                return;
            }

            const newComment = await SocialService.getCommentById(commentId);
            if (!newComment) {
                // comment fetch failed
                return;
            }
            const { createdBy, contentId: commentContentId } = newComment;

            if (commentContentId !== contentId) {
                return;
            }
            // this comment is from the correct content

            const commentParentId = newComment.parentId;
            const parentComment = commentExists(commentList, commentParentId);
            if (commentParentId && !parentComment) {
                // parent comment isn't in the store, so we can't insert the new comment
                SocialDataActions.incrementReplyCount.getActionSender(this).send(contentId);
                return;
            }

            if (parentComment && parentComment.replies.length < parentComment.replyCount) {
                // we only want to increment the reply count because the whole reply list isn't expanded.
                SocialDataActions.incrementReplyCount.getActionSender(this).send(commentParentId);
                return;
            }

            if (!content || !content.id) {
                // no content info in store, fetch from api (should include new comment)
                await this.onCommentComponentConnect(state.me, state.userCommunitySettings, { id: id, market: market });
                return;
            }
            if (!newComment.user) {
                await this.fetchUser(state.users, createdBy);
                const user = this.getCurrentState().users[createdBy];
                newComment.user = user.data;
            }
            SocialDataActions.insertComment.getActionSender(this).send(commentContentId, newComment, commentParentId);
        }
    }

    public isUpdateTypersChanged(expirationTime: number): boolean {
        const state: SocialDataState = this.getCurrentState();
        const contents = state.contents;
        for (const contentId in state.contents) {
            const typingUpdates: TypingUpdates = contents[contentId] && contents[contentId].typingUpdates || {typingUsers: {}} as TypingUpdates;
            const keysNeededToDelete = Object.keys(typingUpdates.typingUsers).filter(userId => typingUpdates.typingUsers[userId].lastTypingTime < expirationTime);
            if (keysNeededToDelete.length > 0) {
                return true;
            }
        }
        return false;
    }

    public async updateTypers() {
        const expireTime: number = Date.now() - 5000;
        if (this.isUpdateTypersChanged(expireTime)) {
            SocialDataActions.updateTypers.getActionSender(this).send(expireTime);
        }
    }

    public broadcastAsTyping(me: User, id: string, market: string, content: Content) {
        const contentId = (content && content.id) || (id && market && id + "_" + market);
        if (contentId && me) {
            SocialService.broadcastTypingNotification(contentId, me.id, me.name, me.avatarUrl);
        }
    }

    public updateResponsiveClass(relativeClass) {
        SocialDataActions.updateResponsiveClass.getActionSender(this).send(relativeClass);
    }

    public deleteMyData(me, users, deletingData) {
        SocialService.deleteMyData(deletingData);
        let newMe = {...users[me.id].data};
        if (deletingData.comment) {
            newMe = {...newMe, postsCount: 0,
                commentSummary: {
                    totalCount: 0,
                    subCommentSummaries: []
                },
                comments: {
                    list: [],
                    asc: false,
                    commentStatus: "On",
                    hasMore: false,
                    hasPrevious: true,
                    moreCount: 0,
                    skip: 0,
                    top: 10,
                    totalCount: 0
                }};
        } if (deletingData.reaction) {
            newMe = {...newMe, likesCount: 0, reactionsCount: 0,
                reactionSummary: {
                    totalCount: 0,
                    subReactionSummaries: []
                }};
        } if (deletingData.follow) {
            newMe.followers.list.forEach(user => {
                const id = user.id;
                const newUser = { data: { isFollowing: false } };
                SocialDataActions.updateUser.getActionSender(this).send(id, newUser);
            }),
            newMe = {...newMe, followingCount: 0,
                following: {
                    list: []
                },
                followSummary: {
                    totalCount: 0,
                    subFollowSummaries: []
                }};
        } if (deletingData.activity) {
            SocialDataActions.deleteActivities.getActionSender(this).send();
        }
        SocialDataActions.updateUser.getActionSender(this).send(me.id,
            {data: newMe});
    }

    public appealComment(commentId: string, activityId: string) {
        SocialService.appealComment(commentId).then(_ => {
            this.appealActivity(activityId);
        });
    }

    public appealMe(activityId: string) {
        SocialService.appealMe().then(_ => {
            this.appealActivity(activityId);
        });
    }

    public appealMyPicture(activityId: string) {
        SocialService.appealMyPicture().then(_ => {
            this.appealActivity(activityId);
        });
    }

    public appealActivity(activityId: string) {
        SocialDataActions.updateActivityAppeal.getActionSender(this).send(activityId, true);
    }

    public checkAndLogReducerError(): void {
        const state: SocialDataState = this.getCurrentState();
        const error = state.error;
        if (!error) { return; }
        SocialDataActions.resetError.getActionSender(this).send();
        const appError = SocialExperienceReducerIncorrectArgumentError;
        appErrorLogger.log({
            ...appError,
            message: "Reducer incorrect argument",
            pb: {
                ...appError.pb,
                customMessage: `Undefined argument "${error.argument}" for "${error.action}" action`
            }
        } as AppError);
    }

    public saveReactionInSuperAppLocalStorage(id: string, reaction?: string): void {
        dispatchBridgeEvent(BridgeEventNames.saveCachedMyReaction, {id: id, reaction: reaction});
    }

    public initializePublisherServiceClient(): void {
        this.publisherServiceClient = new PublisherServiceClient(window.fetch.bind(window), false);
    }

    public async followPublisher(publisherId: string, follow: boolean): Promise<boolean> {
        try {
            await this.publisherServiceClient.publisherUserAction(
                publisherId,
                PublisherActions.More,
                follow ? "POST" : "DELETE"
            );
            return true;
        } catch (error) {
            appErrorLogger.log({
                ...SocialExperiencePublisherFollowUnfollowError,
                message: "Failed to follow/unfollow publisher"
            });
            return false;
        }
    }

    public async getPublisherIsFollowing(publisherId: string): Promise<boolean> {
        try {
            return await this.publisherServiceClient.getUserPublisherStatus(publisherId, PublisherActions.More);
        } catch (error) {
            appErrorLogger.log({
                ...SocialExperiencePublisherIsFollowedFetchError,
                message: "Failed to fetch publisher follow status"
            });
            return false;
        }
    }
}